import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle,
  ChevronDown,
  CreditCard,
  ExternalLink,
  Info,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  RefreshCw,
  Send,
  Shield,
  Sparkles,
  XCircle,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import AuthenticatedLayout from '../components/AuthenticatedLayout'
import CompaniesHouseAuthModal from '../components/CompaniesHouseAuthModal'
import { useUserProfile } from '../context/UserProfileContext'
import { useFormPayment } from '../hooks/useFormPayment'
import { ApiError } from '../utils/api'
import paymentService from '../utils/paymentService'
import TrustpilotReviewModal from '../components/TrustpilotReviewModal'
import {
  companiesHouseAuthService,
  companiesHouseFilingService,
  type ConfirmationStatementFiling,
  type ConfirmationStatementFilingStatus,
  type RegisteredOfficeAddress,
  type RegisteredEmailAddress,
} from '../utils/companiesHouseAuthService'
import { companiesHouseSessionService } from '../utils/companiesHouseSessionService'
import { companyService } from '../utils/companyService'
import { formatSicCodesList, getSicCodeDescription, searchSicCodes } from '../utils/sicCodes'
import type { StoredCompany } from '../types'
import type { CHFlowStep } from '../types/companiesHouseFiling'

// --------------------------------------------------------
// Helpers
// --------------------------------------------------------

function StepIndicator({ step, label, currentStep, stepOrder }: {
  step: CHFlowStep
  label: string
  currentStep: CHFlowStep
  stepOrder: CHFlowStep[]
}) {
  const currentIdx = stepOrder.indexOf(currentStep)
  const thisIdx = stepOrder.indexOf(step)
  const isActive = step === currentStep
  const isCompleted = thisIdx < currentIdx

  return (
    <div className='flex items-center gap-3'>
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors ${
          isActive
            ? 'bg-gray-900 text-white'
            : isCompleted
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-200 text-gray-400'
        }`}
      >
        {isCompleted ? <CheckCircle className='h-4 w-4' /> : thisIdx + 1}
      </div>
      <span className={`text-sm font-medium ${isActive ? 'text-gray-900' : isCompleted ? 'text-green-700' : 'text-gray-400'}`}>
        {label}
      </span>
    </div>
  )
}

// --------------------------------------------------------
// Component
// --------------------------------------------------------

type CHFilingType = 'registered-office' | 'registered-email'

// Confirmation Statement flow: select company → authorise with Companies House
// (needed for the OAuth filing) → review → success. The old filing-type /
// fill-form steps are no longer part of the flow.
const stepOrder: CHFlowStep[] = ['company-select', 'authorize', 'review', 'success']
const stepLabels: Record<CHFlowStep, string> = {
  'company-select': 'Select Company',
  'authorize': 'Authorize',
  'filing-type': 'Filing Type',
  'fill-form': 'Fill Details',
  'review': 'Review & Submit',
  'success': 'Complete',
}

// Persist the wizard position (step + company) so a refresh — or a return from
// the Companies House OAuth redirect / Stripe payment — resumes where the user
// left off (e.g. straight back on Review & Submit) instead of resetting to the
// company picker. Kept intentionally small: the live company data is always
// re-fetched from Companies House on load.
const CH_CS_FLOW_KEY = 'ch-cs-flow'
interface ChCsFlowState { step: CHFlowStep; companyNumber: string }
function loadChCsFlow(): ChCsFlowState | null {
  try {
    const raw = localStorage.getItem(CH_CS_FLOW_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ChCsFlowState
    if (parsed && parsed.step && parsed.companyNumber) return parsed
  } catch { /* ignore malformed */ }
  return null
}
function saveChCsFlow(step: CHFlowStep, companyNumber: string) {
  try { localStorage.setItem(CH_CS_FLOW_KEY, JSON.stringify({ step, companyNumber })) } catch { /* ignore */ }
}
function clearChCsFlow() {
  try { localStorage.removeItem(CH_CS_FLOW_KEY) } catch { /* ignore */ }
}

// Live Confirmation Statement data pulled from Companies House for the selected
// company (dates, registered office, SIC codes, active directors, and PSC).
interface CsDetails {
  companyNumber: string
  companyName: string
  companyStatus?: string
  confirmationStatement: {
    nextMadeUpTo: string | null
    nextDue: string | null
    lastMadeUpTo: string | null
    overdue: boolean
  }
  registeredOffice: { line1: string; line2: string; city: string; county: string; postcode: string; country: string }
  sicCodes: string[]
  directors: CsDirector[]
  psc: { name: string; naturesOfControl: string[]; summary: string }[]
}

interface CsDirector {
  /** As on the register: "SURNAME, Forenames". */
  name: string
  role: string
  appointedOn: string | null
  verified: boolean
  verificationStart: string | null
  /** False for corporate directors, which don't verify with a personal code. */
  requiresVerification?: boolean
  verificationDueOn?: string | null
  /** Companies House only publishes month and year. */
  dateOfBirth?: { month: number; year: number } | null
}

// Official GOV.UK identity-verification service for Companies House directors.
const COMPANIES_HOUSE_VERIFY_URL = 'https://www.gov.uk/guidance/verifying-your-identity-for-companies-house'

// Companies House personal codes are 11 letters and numbers.
const PERSONAL_CODE_PATTERN = /^[A-Z0-9]{11}$/

function needsVerification(d: CsDirector): boolean {
  return !d.verified && d.requiresVerification !== false
}

function isPastIsoDate(value: string | undefined): boolean {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const d = new Date(value)
  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now()
}

// The register's month/year of birth must agree with the full date entered.
function dobMatchesRegister(d: CsDirector, dob: string | undefined): boolean {
  if (!d.dateOfBirth || !isPastIsoDate(dob)) return true
  const [year, month] = (dob as string).split('-').map(Number)
  return year === d.dateOfBirth.year && month === d.dateOfBirth.month
}

function fmtMonthYear(dob: { month: number; year: number }): string {
  return new Date(Date.UTC(dob.year, dob.month - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

// Collect the non-empty lines of a registered office address (handles both the
// CompanyAddress and CompaniesHouseAddress field shapes).
function formatRegisteredOffice(office: any): string[] {
  if (!office) return []
  return [
    office.premises,
    office.addressLine1 || office.line1,
    office.addressLine2 || office.line2,
    office.locality || office.city,
    office.region || office.county,
    office.postalCode || office.postcode,
    office.country,
  ].filter((l): l is string => !!l && String(l).trim().length > 0)
}

// Format an ISO date (YYYY-MM-DD) as dd/mm/yyyy for display.
function fmtCsDate(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

// Derive the filing-status view (statement date, deadline, days left, overdue)
// from the Companies House confirmation_statement dates.
function getCsStatus(cs: CsDetails['confirmationStatement'] | null | undefined) {
  if (!cs) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = cs.nextDue ? new Date(cs.nextDue) : null
  let daysLeft: number | null = null
  if (due && !Number.isNaN(due.getTime())) {
    due.setHours(0, 0, 0, 0)
    daysLeft = Math.round((due.getTime() - today.getTime()) / (24 * 60 * 60 * 1000))
  }
  const overdue = !!cs.overdue || (daysLeft !== null && daysLeft < 0)
  return {
    statementDate: fmtCsDate(cs.nextMadeUpTo),
    deadline: fmtCsDate(cs.nextDue),
    daysLeft,
    overdue,
  }
}

interface CHPricingPlan {
  _id: string
  name: string
  slug: string
  productType: string
  price: number
  currency: string
  billingCycle: string
  features: string[]
  isActive: boolean
  sortOrder?: number
}

export default function CompaniesHouseFilingPage() {
  const navigate = useNavigate()
  const { user } = useUserProfile()
  const { checkPayment, isPaid } = useFormPayment()

  // Flow state
  const [currentStep, setCurrentStep] = useState<CHFlowStep>('company-select')
  const [filingType, setFilingType] = useState<CHFilingType | null>(null)

  // Company state
  const [companies, setCompanies] = useState<StoredCompany[]>([])
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true)
  const [selectedCompany, setSelectedCompany] = useState<StoredCompany | null>(null)
  const [companyNumber, setCompanyNumber] = useState('')

  // Auth state
  const [hasValidToken, setHasValidToken] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(false)
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false)
  const [authUrl, setAuthUrl] = useState<string | null>(null)
  const [isRequestingAuth, setIsRequestingAuth] = useState(false)
  // After returning from the Companies House OAuth redirect we only know the
  // company number; re-select the full company once the list has loaded.
  const [resumeCompanyNumber, setResumeCompanyNumber] = useState<string | null>(null)

  // Payment / plan state
  const [chPlans, setChPlans] = useState<CHPricingPlan[]>([])
  const [showReviewPrompt, setShowReviewPrompt] = useState(false)
  // Which channel the backend files through. `feeIncluded` means we settle the
  // Companies House fee from our credit account, so our price is all the user
  // pays and there is no GOV.UK Pay redirect.
  // `checkFailed` means the backend couldn't be asked, as opposed to it
  // answering that gateway filing is off.
  const [csCapability, setCsCapability] = useState<{
    feeIncluded: boolean
    requiresCompanyAuthCode: boolean
    checkFailed: boolean
  }>({ feeIncluded: false, requiresCompanyAuthCode: false, checkFailed: false })
  const [companyAuthCode, setCompanyAuthCode] = useState('')
  const [isLoadingPlans, setIsLoadingPlans] = useState(false)
  const [selectedPlanSlug, setSelectedPlanSlug] = useState<string | null>(null)

  // Form data
  const [address, setAddress] = useState<RegisteredOfficeAddress>({
    premises: '',
    address_line_1: '',
    address_line_2: '',
    locality: '',
    region: '',
    postal_code: '',
    country: 'United Kingdom',
    accept_appropriate_office_address_statement: false,
  })
  const [emailData, setEmailData] = useState<RegisteredEmailAddress>({
    registered_email_address: '',
    accept_appropriate_email_address_statement: false,
  })

  // Submission
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [resultTransactionId, setResultTransactionId] = useState<string | null>(null)
  const [filingStatus, setFilingStatus] = useState<ConfirmationStatementFilingStatus | null>(null)
  // Gateway filing just submitted: Companies House has received it and decides
  // later, so the success step shows its live status rather than "accepted".
  const [submittedFiling, setSubmittedFiling] = useState<ConfirmationStatementFiling | null>(null)
  const [resultMessage, setResultMessage] = useState<string | null>(null)
  const [isCheckingFilingStatus, setIsCheckingFilingStatus] = useState(false)

  // Review (Page 2) state
  const [confirmReviewed, setConfirmReviewed] = useState(false)

  // Live Companies House confirmation-statement details for the selected company.
  const [csDetails, setCsDetails] = useState<CsDetails | null>(null)
  const [isLoadingCsDetails, setIsLoadingCsDetails] = useState(false)
  // Verification details captured per unverified director (by register name),
  // filed with the statement as a ConfirmationAndVerificationStatement.
  const [personalCodes, setPersonalCodes] = useState<Record<string, string>>({})
  const [directorDobs, setDirectorDobs] = useState<Record<string, string>>({})

  // Inline SIC-code editor (opened from the review page — no navigation away).
  const [sicModalOpen, setSicModalOpen] = useState(false)
  const [editedSicCodes, setEditedSicCodes] = useState<string[]>([])
  const [sicSearch, setSicSearch] = useState('')
  const [isSavingSic, setIsSavingSic] = useState(false)

  const openSicEditor = () => {
    setEditedSicCodes(csDetails?.sicCodes ?? selectedCompany?.sicCodes ?? [])
    setSicSearch('')
    setSicModalOpen(true)
  }

  const saveSicCodes = async () => {
    if (!selectedCompany?._id) return
    setIsSavingSic(true)
    try {
      await companyService.updateCompany(selectedCompany._id, { sicCodes: editedSicCodes })
      // Reflect the change locally so the review page updates immediately.
      setSelectedCompany((prev) => (prev ? { ...prev, sicCodes: editedSicCodes } : prev))
      setCsDetails((prev) => (prev ? { ...prev, sicCodes: editedSicCodes } : prev))
      setSicModalOpen(false)
      toast.success('SIC codes updated.')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update SIC codes.')
    } finally {
      setIsSavingSic(false)
    }
  }

  // Real director identity-verification status comes from Companies House.
  // `every` on an empty list is true, so companies with no directors aren't blocked.
  const directorsNeedingVerification = (csDetails?.directors ?? []).filter(needsVerification)
  const allDirectorsVerified = directorsNeedingVerification.length === 0
  // On the gateway channel a director's verification statement is filed with
  // this Confirmation Statement, so complete details are as good as verified.
  // The REST channel can't carry them, so there they must verify first.
  const verificationDetailsComplete = directorsNeedingVerification.every(
    (d) =>
      PERSONAL_CODE_PATTERN.test(personalCodes[d.name] ?? '') &&
      isPastIsoDate(directorDobs[d.name]) &&
      dobMatchesRegister(d, directorDobs[d.name])
  )
  const directorsReady = allDirectorsVerified || (csCapability.feeIncluded && verificationDetailsComplete)

  // ========================================================
  // Load companies
  // ========================================================
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setIsLoadingCompanies(true)
        const { response } = await companyService.getAllCompanies(1, 50)
        const responseData = response as any
        const actualData = responseData?.response?.data ?? responseData?.data ?? responseData
        let companiesData: StoredCompany[] = []

        if (Array.isArray(actualData)) {
          companiesData = actualData
        } else if (actualData && typeof actualData === 'object') {
          companiesData =
            actualData.companies ?? actualData.data ?? actualData.docs ?? actualData.items ?? []
        }

        setCompanies(
          companiesData.map((c) => ({
            ...c,
            companyName: c.companyName || c.name || `Company ${c.companyNumber || c._id}`,
          }))
        )
      } catch {
        toast.error('Unable to fetch companies.')
      } finally {
        setIsLoadingCompanies(false)
      }
    }

    loadCompanies()
  }, [])

  // ========================================================
  // Resume company selection after an OAuth redirect
  // ========================================================
  useEffect(() => {
    if (!resumeCompanyNumber || companies.length === 0) return
    const match = companies.find((c) => c.companyNumber === resumeCompanyNumber)
    if (match) {
      setSelectedCompany(match)
      setCompanyNumber(match.companyNumber)
    }
    setResumeCompanyNumber(null)
  }, [resumeCompanyNumber, companies])

  // ========================================================
  // Load live Confirmation Statement details from Companies House.
  // Reusable so the review page can re-check director verification status.
  // ========================================================
  const loadCsDetails = useCallback(async (num: string) => {
    setIsLoadingCsDetails(true)
    try {
      const { response } = await companyService.getConfirmationStatementDetails(num)
      const data = (response as any)?.data ?? response
      setCsDetails(data && typeof data === 'object' ? (data as CsDetails) : null)
    } catch {
      setCsDetails(null)
    } finally {
      setIsLoadingCsDetails(false)
    }
  }, [])

  useEffect(() => {
    const num = selectedCompany?.companyNumber
    if (!num) {
      setCsDetails(null)
      return
    }
    setCsDetails(null)
    void loadCsDetails(num)
  }, [selectedCompany?.companyNumber, loadCsDetails])

  // ========================================================
  // Check existing session on mount
  // ========================================================
  useEffect(() => {
    const session = companiesHouseSessionService.getSession()
    if (session.isAuthorized) {
      setHasValidToken(true)
      if (session.companyNumber) setCompanyNumber(session.companyNumber)
    }

    // 1) Returning from the Companies House OAuth sign-in. Restore the company
    //    we were filing for and, now that authorisation is complete, take the
    //    user straight on to Review & Submit (persisted, so a refresh stays here).
    const redirectPath = sessionStorage.getItem('companiesHouseAuthRedirect')
    const storedNum = sessionStorage.getItem('companiesHouseCompanyNumber')
    if (redirectPath && redirectPath === window.location.pathname) {
      sessionStorage.removeItem('companiesHouseAuthRedirect')
      if (storedNum) {
        setCompanyNumber(storedNum)
        setResumeCompanyNumber(storedNum)
        sessionStorage.removeItem('companiesHouseCompanyNumber')
      }
      const refreshedSession = companiesHouseSessionService.getSession()
      const authorized = refreshedSession.isAuthorized
      if (authorized) setHasValidToken(true)
      const nextStep: CHFlowStep = authorized ? 'review' : 'authorize'
      setCurrentStep(nextStep)
      if (storedNum) saveChCsFlow(nextStep, storedNum)
      return
    }

    // 2) A normal refresh or a return from Stripe payment. Resume from the URL
    //    (?step=&companyNumber= carried on the payment redirect) or, failing
    //    that, the persisted flow state.
    const urlParams = new URLSearchParams(window.location.search)
    const urlStep = urlParams.get('step') as CHFlowStep | null
    const urlCompany = urlParams.get('companyNumber')
    const persisted = loadChCsFlow()
    // A company passed in the URL (e.g. "view" from Filing History) wins over
    // any stale persisted flow for a *different* company.
    const resumeCompany = urlCompany || persisted?.companyNumber || null
    const persistedStepApplies = !urlCompany || urlCompany === persisted?.companyNumber
    const resumeStep = urlStep || (persistedStepApplies ? persisted?.step : null) || null
    if (!resumeCompany) return

    // Preselect the company even when no step is given, so arriving with just
    // ?companyNumber= opens the wizard on that company rather than a blank picker.
    setCompanyNumber(resumeCompany)
    setResumeCompanyNumber(resumeCompany)
    if (resumeStep && resumeStep !== 'company-select' && resumeStep !== 'success') {
      setCurrentStep(resumeStep)
      saveChCsFlow(resumeStep, resumeCompany)
    }
  }, [])

  // ========================================================
  // Persist wizard position (step + company) on every change so a refresh
  // resumes in place. Cleared once the flow completes (success) or resets.
  // ========================================================
  useEffect(() => {
    if (currentStep === 'success') { clearChCsFlow(); return }
    const num = selectedCompany?.companyNumber || companyNumber
    if (num && currentStep !== 'company-select') {
      saveChCsFlow(currentStep, num)
    }
  }, [currentStep, selectedCompany?.companyNumber, companyNumber])

  // ========================================================
  // Verify the Companies House authorisation against the backend once the
  // company is known. Without this a resumed/refreshed flow shows a stale
  // "Authorized ✓" from localStorage and only discovers the token expired when
  // the submit fails. Runs once per company number.
  // ========================================================
  const verifiedAuthForRef = useRef<string | null>(null)
  useEffect(() => {
    if (currentStep !== 'authorize' && currentStep !== 'review') return
    const num = (selectedCompany?.companyNumber || companyNumber || '').trim().toUpperCase()
    if (!num || verifiedAuthForRef.current === num) return
    verifiedAuthForRef.current = num
    void checkTokenStatus(num)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedCompany?.companyNumber, companyNumber])

  // ========================================================
  // Reflect payment status on the Review & Submit step (so returning from
  // Stripe shows "Confirm & Submit" instead of "Confirm & Pay"). Uses the same
  // per-company formId (company _id) as the checkout.
  // ========================================================
  useEffect(() => {
    if (currentStep !== 'review' || chPlans.length === 0) return
    // The subscription is keyed by the company _id (the payment formId), so wait
    // for the selected company to resolve before checking — the 8-char company
    // number would never match.
    const formId = selectedCompany?._id
    if (formId) void checkPayment(formId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedCompany?._id, chPlans.length])

  // ========================================================
  // Load the filing capability (which channel, who pays Companies House)
  // ========================================================
  // Re-checked on reaching Review: a call that failed on first load (e.g.
  // during a token refresh) would otherwise leave the gateway fields off.
  useEffect(() => {
    if (currentStep !== 'company-select' && currentStep !== 'review') return
    const loadCapability = async () => {
      try {
        const resp = await companiesHouseFilingService.getConfirmationStatementCapability()
        const data = ((resp.response as any)?.data ?? resp.response) as {
          feeIncluded?: boolean
          requiresCompanyAuthCode?: boolean
        }
        setCsCapability({
          feeIncluded: data?.feeIncluded === true,
          requiresCompanyAuthCode: data?.requiresCompanyAuthCode === true,
          checkFailed: false,
        })
      } catch {
        // Keep the REST default, but say so on Review instead of silently
        // hiding the gateway fields.
        setCsCapability((prev) => ({ ...prev, checkFailed: true }))
      }
    }
    void loadCapability()
  }, [currentStep])

  // ========================================================
  // Load Companies House pricing plans
  // ========================================================
  useEffect(() => {
    const loadPlans = async () => {
      try {
        setIsLoadingPlans(true)
        const response = await paymentService.listPricingPlans()
        const data = response as Record<string, any>

        // Try known keys for Companies House plans. The live price list groups
        // the single CS plan under its own product type, so that key comes first.
        const knownKeys = [
          'confirmation-statement',
          'confirmationStatement',
          'companies-house',
          'companiesHouse',
          'companies_house',
          'ch',
          'companies house',
        ]
        let plans: CHPricingPlan[] = []

        for (const key of knownKeys) {
          if (Array.isArray(data?.[key]) && data[key].length > 0) {
            plans = data[key]
            break
          }
        }

        // Fallback: search all keys for any plan whose productType contains 'compan' or 'ch'
        if (plans.length === 0 && data && typeof data === 'object') {
          for (const [key, value] of Object.entries(data)) {
            if (Array.isArray(value) && value.length > 0) {
              const firstPlan = value[0] as any
              const pt = (firstPlan?.productType || key || '').toLowerCase()
              if (pt.includes('compan') || pt.includes('confirmation') || pt === 'ch') {
                plans = value as CHPricingPlan[]
                break
              }
            }
          }
        }

        const sorted = plans.sort((a: CHPricingPlan, b: CHPricingPlan) => {
          if (a.sortOrder && b.sortOrder) return a.sortOrder - b.sortOrder
          return a.price - b.price
        })
        setChPlans(sorted)
        if (sorted.length > 0 && sorted[0]) {
          setSelectedPlanSlug(sorted[0].slug)
        }
      } catch {
        // Non-critical — plans may not exist yet
      } finally {
        setIsLoadingPlans(false)
      }
    }
    loadPlans()
  }, [])

  // ========================================================
  // Auth handler
  // ========================================================
  const handleRequestAuth = async () => {
    const num = companyNumber.trim().toUpperCase()
    if (!num) {
      toast.error('Please enter or select a company number first.')
      return
    }
    if (!/^[A-Z0-9]{8}$/i.test(num)) {
      toast.error('Company number must be 8 alphanumeric characters.')
      return
    }

    setIsRequestingAuth(true)
    setIsAuthModalOpen(true)

    try {
      const envelope = await companiesHouseAuthService.authorize(num)
      const authResponse = envelope.response as any

      const url = authResponse?.data?.authUrl || authResponse?.authUrl
      if (url) {
        setAuthUrl(url)
      } else {
        toast.error('Failed to generate authorization URL.')
        setIsAuthModalOpen(false)
      }
    } catch (error) {
      console.error('Authorization error:', error)
      if (error instanceof ApiError) {
        toast.error(error.message || 'Failed to initiate authorization.')
      } else {
        toast.error('An unexpected error occurred.')
      }
      setIsAuthModalOpen(false)
    } finally {
      setIsRequestingAuth(false)
    }
  }

  // The backend is the source of truth. The local `companiesHouseAuthorized`
  // flag outlives the actual CH token, so trusting it first made the wizard show
  // "Authorized ✓" against an expired token and then fail at submit with
  // `authorization_required`. Only fall back to the local flag if the check
  // itself couldn't run (offline/500).
  const checkTokenStatus = async (num?: string) => {
    setIsCheckingAuth(true)
    try {
      const target = (num ?? companyNumber ?? '').trim().toUpperCase()
      const hasToken = await companiesHouseAuthService.hasValidToken(target || undefined)
      setHasValidToken(hasToken)
      if (!hasToken) companiesHouseSessionService.clearSession()
      return hasToken
    } catch {
      const session = companiesHouseSessionService.getSession()
      setHasValidToken(session.isAuthorized)
      return session.isAuthorized
    } finally {
      setIsCheckingAuth(false)
    }
  }

  // ========================================================
  // Companies House says the company isn't authorised (token missing/expired or
  // scoped to another company). The local "authorized" flag is stale, so clear
  // it and take the user back to the Authorize step — otherwise they're stranded
  // on Review & Submit with an error they cannot act on.
  // ========================================================
  const handleAuthorizationRequired = () => {
    companiesHouseSessionService.clearSession()
    setHasValidToken(false)
    setCurrentStep('authorize')
    const num = selectedCompany?.companyNumber || companyNumber
    if (num) saveChCsFlow('authorize', num)
    toast.error('Your Companies House authorisation has expired. Please authorise again to file.')
  }

  // ========================================================
  // Submit
  // ========================================================
  // Platform payment handoff — redirect to Stripe checkout. Mirrors the Self
  // Assessment flow: pay first, then return to Review & Submit (via the
  // persisted step) to actually file. Returns true when redirecting.
  const startCheckout = async (formId: string): Promise<boolean> => {
    const planSlug = selectedPlanSlug || (chPlans[0] ? chPlans[0].slug : null)
    if (!planSlug) {
      toast.error('Please select a plan to continue.')
      return false
    }
    const num = companyNumber.trim().toUpperCase()
    // Land back on Review & Submit after payment (not /history) so the
    // user can complete the filing now that it shows as paid.
    const successParams = new URLSearchParams({
      plan: planSlug,
      formId,
      source: 'companies-house',
      companyNumber: num,
      step: 'review',
    })
    // {CHECKOUT_SESSION_ID} must stay literal for Stripe to substitute it,
    // so it's appended outside URLSearchParams (which would encode the braces).
    const successUrl = `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}&${successParams.toString()}`
    const cancelParams = new URLSearchParams({ companyNumber: num, step: 'review' })
    const cancelUrl = `${window.location.origin}/companies-house-filing?${cancelParams.toString()}`
    const checkoutData = await paymentService.createCheckout(planSlug, {
      successUrl,
      cancelUrl,
      customerEmail: user?.email || '',
      formId,
    })
    if (checkoutData.url) {
      window.location.href = checkoutData.url
      return true
    }
    toast.error('Could not start payment. Please try again.')
    return false
  }

  const handleSubmit = async () => {
    if (!confirmReviewed) {
      toast.error('Please confirm you have reviewed the company information.')
      return
    }
    if (!directorsReady) {
      toast.error(
        csCapability.feeIncluded
          ? "Enter the personal code and date of birth for each director who hasn't verified their identity yet."
          : 'All directors must have a verified identity with Companies House before submitting.'
      )
      return
    }

    // The company record's _id is the payment formId — Confirmation Statements
    // are billed per company and there's no per-filing document (unlike
    // CT600/SA). The backend validates this against the companies collection.
    const formId = selectedCompany?._id || companyNumber

    setIsSubmitting(true)
    try {
      if (chPlans.length > 0) {
        const paid = await checkPayment(formId)
        if (!paid) {
          await startCheckout(formId)
          return
        }
      }

      // Fee-inclusive route: we file through the Companies House XML gateway
      // and the statutory fee is billed to our credit account, so the single
      // payment above is everything the user owes — no GOV.UK Pay redirect.
      if (csCapability.feeIncluded) {
        if (csCapability.requiresCompanyAuthCode && !companyAuthCode.trim()) {
          toast.error('Enter your Companies House authentication code to file.')
          return
        }
        const gwResp = await companiesHouseFilingService.submitConfirmationStatement({
          companyNumber: companyNumber.trim().toUpperCase(),
          ...(selectedCompany?.companyName ? { companyName: selectedCompany.companyName } : {}),
          ...(csDetails?.confirmationStatement?.nextMadeUpTo
            ? { statementDate: csDetails.confirmationStatement.nextMadeUpTo }
            : {}),
          companyAuthCode: companyAuthCode.trim(),
          ...(directorsNeedingVerification.length > 0
            ? {
                personalCodes: directorsNeedingVerification.map((d) => ({
                  name: d.name,
                  code: personalCodes[d.name] ?? '',
                  dateOfBirth: directorDobs[d.name] ?? '',
                })),
              }
            : {}),
        })
        const gwData = gwResp.response
        const gwStatus = gwData?.status

        if (gwStatus === 'rejected') {
          // Refused on receipt, so nothing was delivered — stay on Review to fix it.
          const detail = Array.isArray(gwData?.errors) && gwData.errors.length > 0
            ? gwData.errors.map((e) => e.message).join('; ')
            : 'Companies House rejected the Confirmation Statement.'
          toast.error(detail)
          return
        }

        // Companies House has RECEIVED it — that is not acceptance. They decide
        // later; the backend checks automatically and the Filings tab follows it.
        setSubmittedFiling(gwData?.filing ?? null)
        setFilingStatus(gwData?.filing?.status ?? 'pending')
        setResultTransactionId(gwData?.submissionNumber ?? null)
        setResultMessage(gwData?.message ?? null)
        setCurrentStep('success')
        // No review prompt yet: received isn't filed. It opens if a status
        // check comes back accepted.
        // Strip the payment redirect params (?step=review) so a refresh can't
        // bounce back to Review and file a duplicate.
        clearChCsFlow()
        navigate('/companies-house-filing', { replace: true })
        toast.success(
          gwData?.filing
            ? 'Submitted to Companies House — awaiting their decision.'
            : gwData?.message || 'Confirmation Statement received.'
        )
        return
      }

      // File the confirmation statement via the Companies House transactions
      // REST API (SDK). Targets the CH sandbox unless the backend CH_CS_REST_LIVE
      // flag is set, so it cannot reach the live register by default.
      const restResp = await companiesHouseFilingService.fileConfirmationStatementRest({
        companyNumber: companyNumber.trim().toUpperCase(),
        ...(csDetails?.confirmationStatement?.nextMadeUpTo
          ? { madeUpToDate: csDetails.confirmationStatement.nextMadeUpTo }
          : {}),
      })
      const data = (restResp.response as any)?.data ?? restResp.response
      const status = data?.status as string | undefined

      if (status === 'payment_required' && data?.paymentUrl) {
        // Hand off to GOV.UK Pay for the Companies House statutory fee.
        window.location.href = data.paymentUrl
        return
      }
      if (status === 'authorization_required') {
        handleAuthorizationRequired()
        return
      }
      // Only an explicit 'submitted' counts as filed. Everything else is a
      // failure and must NOT show the success screen — an unrecognised status
      // used to fall through here and tell the user their statement was
      // received when Companies House had actually rejected or never got it.
      if (status !== 'submitted') {
        toast.error(data?.message || 'Companies House could not accept this confirmation statement.')
        return
      }

      setFilingStatus('accepted')
      setResultTransactionId(data?.transactionId || formId)
      setCurrentStep('success')
      setShowReviewPrompt(true)
      // Filing is done — drop the persisted flow and strip the payment redirect
      // params (?step=review) so a refresh can't bounce back to Review and file
      // a duplicate.
      clearChCsFlow()
      navigate('/companies-house-filing', { replace: true })
    } catch (error) {
      console.error('Confirmation Statement submission error:', error)
      if (error instanceof ApiError) {
        // The backend returns these as HTTP 4xx, so they arrive here as a thrown
        // ApiError rather than a resolved response — the status checks above
        // never see them. Read the status off the error envelope.
        const errStatus =
          (error.response as any)?.response?.status ?? (error.response as any)?.status
        if (errStatus === 'authorization_required') {
          handleAuthorizationRequired()
          return
        }
        // Coverage can say "paid" while every CS payment has already been spent
        // on an earlier filing — the backend is the authority, so pay again.
        if (error.status === 402 || errStatus === 'payment_required') {
          toast.info('This filing needs a new payment — taking you to checkout.')
          await startCheckout(formId)
          return
        }
        toast.error(error.message || 'Failed to submit Confirmation Statement.')
      } else {
        toast.error('An unexpected error occurred during submission.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Ask the backend for the latest Companies House decision on the filing just submitted.
  const refreshSubmittedFiling = async () => {
    if (!submittedFiling) return
    setIsCheckingFilingStatus(true)
    try {
      const resp = await companiesHouseFilingService.getConfirmationStatementSubmissionStatus(
        submittedFiling.submissionNumber
      )
      const filing = resp.response?.filing
      if (filing) {
        setSubmittedFiling(filing)
        setFilingStatus(filing.status)
        if (filing.status === 'accepted') {
          setShowReviewPrompt(true)
        } else if (filing.status === 'pending') {
          toast.info('Still with Companies House — most filings are decided within 24 hours.')
        }
      }
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'Could not check the filing status.')
    } finally {
      setIsCheckingFilingStatus(false)
    }
  }

  // ========================================================
  // Navigation
  // ========================================================
  const canAdvance = (): boolean => {
    switch (currentStep) {
      case 'company-select':
        return companyNumber.trim().length === 8
      case 'review':
        return confirmReviewed && directorsReady
      case 'authorize':
        return hasValidToken
      case 'filing-type':
        return filingType !== null
      case 'fill-form':
        if (filingType === 'registered-office') {
          return !!(address.address_line_1 && address.locality && address.postal_code && address.accept_appropriate_office_address_statement)
        }
        return !!(emailData.registered_email_address && emailData.accept_appropriate_email_address_statement)
      default:
        return false
    }
  }

  const handleNext = async () => {
    if (currentStep === 'authorize' && !hasValidToken) {
      const tokenValid = await checkTokenStatus()
      if (!tokenValid) {
        toast.error('Please complete Companies House authorization first.')
        return
      }
    }

    if (currentStep === 'review') {
      await handleSubmit()
      return
    }

    const idx = stepOrder.indexOf(currentStep)
    const next = stepOrder[idx + 1]
    if (next) {
      // Entering the authorise step: reflect any existing token up front.
      if (next === 'authorize' && !hasValidToken) void checkTokenStatus()
      setCurrentStep(next)
    }
  }

  const handleBack = () => {
    const idx = stepOrder.indexOf(currentStep)
    const prev = idx > 0 ? stepOrder[idx - 1] : undefined
    if (prev) setCurrentStep(prev)
  }

  // Header arrow: jump straight back to "Select Company" from anywhere in the
  // wizard. Clears the persisted position too, otherwise a refresh would resume
  // at the step we just left. On the first step there is nowhere further back
  // inside the wizard, so it leaves the page.
  const handleHeaderBack = () => {
    if (currentStep === 'company-select') {
      navigate(-1)
      return
    }
    clearChCsFlow()
    setCurrentStep('company-select')
  }

  // ========================================================
  // Render steps
  // ========================================================
  const renderContent = () => {
    switch (currentStep) {
      // ----- Company Select -----
      case 'company-select': {
        const csStatus = getCsStatus(csDetails?.confirmationStatement)
        return (
          <div className='space-y-6'>
            <div>
              <h2 className='text-xl font-bold text-gray-900'>Start a Confirmation Statement</h2>
              <p className='mt-1 text-sm text-gray-600'>
                Select the company you want to file a Confirmation Statement for. If the company isn't listed, click Add Company to add it to your account.
              </p>
            </div>

            <div>
              <div className='mb-2 flex items-center justify-between gap-3'>
                <label className='block text-sm font-medium text-gray-900'>Your Company</label>
                <button
                  type='button'
                  onClick={() => navigate('/add-company')}
                  className='inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-50 transition'
                >
                  <Building2 className='h-4 w-4' />
                  Add Company
                </button>
              </div>
              <div className='relative'>
                <Building2 className='pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
                <select
                  value={selectedCompany?._id || ''}
                  onChange={(e) => {
                    const company = companies.find((c) => c._id === e.target.value)
                    if (company) {
                      setSelectedCompany(company)
                      setCompanyNumber(company.companyNumber)
                    } else {
                      setSelectedCompany(null)
                      setCompanyNumber('')
                    }
                  }}
                  className='w-full appearance-none rounded-xl border border-gray-300 bg-white py-3.5 pl-12 pr-10 text-sm font-medium text-gray-900 shadow-sm hover:border-gray-400 focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 transition'
                >
                  <option value=''>
                    {isLoadingCompanies ? 'Loading...' : companies.length === 0 ? 'No companies yet — click Add Company' : 'Select a company'}
                  </option>
                  {companies.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.companyName || c.name} ({c.companyNumber})
                    </option>
                  ))}
                </select>
                <ChevronDown className='pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400' />
              </div>
            </div>

            {selectedCompany && (
              <div className='space-y-4 rounded-xl border border-gray-200 bg-gray-50 p-5'>
                {isLoadingCsDetails ? (
                  <p className='text-sm text-gray-500'>Loading Confirmation Statement details from Companies House…</p>
                ) : csStatus ? (
                  <>
                    <div className='grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm'>
                      <div>
                        <p className='text-gray-500'>Company</p>
                        <p className='mt-0.5 font-semibold text-gray-900'>{selectedCompany.companyName || selectedCompany.name}</p>
                      </div>
                      <div>
                        <p className='text-gray-500'>Company number</p>
                        <p className='mt-0.5 font-semibold text-gray-900'>{selectedCompany.companyNumber}</p>
                      </div>
                      <div>
                        <p className='text-gray-500'>Confirmation Statement date</p>
                        <p className='mt-0.5 font-semibold text-gray-900'>{csStatus.statementDate}</p>
                      </div>
                      <div>
                        <p className='text-gray-500'>Filing deadline</p>
                        <p className='mt-0.5 font-semibold text-gray-900'>{csStatus.deadline}</p>
                      </div>
                    </div>
                    <div className='flex items-center gap-2'>
                      <p className='text-sm text-gray-500'>Filing status:</p>
                      {csStatus.daysLeft !== null && !csStatus.overdue ? (
                        <span className='inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-sm font-semibold text-green-700'>
                          <CheckCircle className='h-4 w-4' />
                          Due in {csStatus.daysLeft} {csStatus.daysLeft === 1 ? 'day' : 'days'}
                        </span>
                      ) : csStatus.overdue && csStatus.daysLeft !== null ? (
                        <span className='inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1 text-sm font-semibold text-red-700'>
                          <AlertCircle className='h-4 w-4' />
                          Overdue by {Math.abs(csStatus.daysLeft)} {Math.abs(csStatus.daysLeft) === 1 ? 'day' : 'days'}
                        </span>
                      ) : (
                        <span className='text-sm font-medium text-gray-500'>Not available</span>
                      )}
                    </div>
                  </>
                ) : (
                  <p className='text-sm text-gray-500'>
                    Couldn't load Confirmation Statement details from Companies House for this company.
                  </p>
                )}
              </div>
            )}
          </div>
        )
      }

      // ----- Authorize -----
      case 'authorize':
        return (
          <div className='space-y-6'>
            <div className='text-center'>
              <h2 className='text-xl font-bold text-gray-900'>Companies House Authorization</h2>
              <p className='mt-1 text-sm text-gray-600'>
                Authorize with Companies House to file on behalf of company <strong>{companyNumber}</strong>.
              </p>
            </div>

            <div className={`mx-auto max-w-lg rounded-lg border-2 p-6 ${hasValidToken ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
              <div className='flex flex-col items-center text-center gap-3'>
                <Shield className={`h-10 w-10 ${hasValidToken ? 'text-green-600' : 'text-amber-600'}`} />
                <h3 className={`text-base font-semibold ${hasValidToken ? 'text-green-900' : 'text-amber-900'}`}>
                  {hasValidToken ? 'Authorization Complete' : 'Authorization Required'}
                </h3>
                <p className={`text-sm ${hasValidToken ? 'text-green-700' : 'text-amber-700'}`}>
                  {hasValidToken
                    ? 'You are authorized to file with Companies House. Click "Next" to continue.'
                    : 'You need to authorize with Companies House to submit filings. This will redirect you to the Companies House sign-in page.'}
                </p>
                {!hasValidToken && (
                  <button
                    type='button'
                    onClick={handleRequestAuth}
                    disabled={isRequestingAuth || isCheckingAuth}
                    className='mt-2 flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 transition-colors'
                  >
                    {isRequestingAuth || isCheckingAuth ? (
                      <Loader2 className='h-4 w-4 animate-spin' />
                    ) : (
                      <Shield className='h-4 w-4' />
                    )}
                    Authorize with Companies House
                  </button>
                )}
                {hasValidToken && <CheckCircle className='h-6 w-6 text-green-600' />}
              </div>
            </div>

            {!hasValidToken && (
              <div className='flex flex-col items-center gap-2'>
                <button
                  type='button'
                  onClick={() => { void checkTokenStatus() }}
                  disabled={isCheckingAuth}
                  className='text-sm text-gray-600 underline hover:text-gray-900'
                >
                  {isCheckingAuth ? 'Checking...' : 'I\'ve already authorized — refresh status'}
                </button>
              </div>
            )}
          </div>
        )

      // ----- Filing Type -----
      case 'filing-type':
        return (
          <div className='space-y-6'>
            <div>
              <h2 className='text-xl font-bold text-gray-900'>Select Filing Type</h2>
              <p className='mt-1 text-sm text-gray-600'>
                What would you like to file with Companies House?
              </p>
            </div>

            <div className='grid grid-cols-1 gap-4'>
              <button
                type='button'
                onClick={() => setFilingType('registered-office')}
                className={`flex items-start gap-4 rounded-xl border-2 p-5 text-left transition-all ${
                  filingType === 'registered-office'
                    ? 'border-gray-900 bg-gray-50 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className={`rounded-lg p-2.5 ${filingType === 'registered-office' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  <MapPin className='h-5 w-5' />
                </div>
                <div>
                  <h3 className='text-sm font-semibold text-gray-900'>Change Registered Office Address</h3>
                  <p className='mt-1 text-xs text-gray-500'>
                    File a change of registered office address with Companies House.
                  </p>
                </div>
              </button>

              <button
                type='button'
                onClick={() => setFilingType('registered-email')}
                className={`flex items-start gap-4 rounded-xl border-2 p-5 text-left transition-all ${
                  filingType === 'registered-email'
                    ? 'border-gray-900 bg-gray-50 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className={`rounded-lg p-2.5 ${filingType === 'registered-email' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  <Mail className='h-5 w-5' />
                </div>
                <div>
                  <h3 className='text-sm font-semibold text-gray-900'>Change Registered Email Address</h3>
                  <p className='mt-1 text-xs text-gray-500'>
                    Update the registered email address for your company.
                  </p>
                </div>
              </button>
            </div>

            {/* Plan Selection */}
            <div className='space-y-3'>
              <div className='flex items-center gap-2'>
                <Sparkles className='h-4 w-4 text-gray-500' />
                <h3 className='text-sm font-semibold text-gray-900'>Platform Filing Plan</h3>
              </div>
              {isLoadingPlans ? (
                <div className='flex items-center justify-center rounded-lg border border-gray-200 py-6'>
                  <Loader2 className='h-5 w-5 animate-spin text-gray-400' />
                  <span className='ml-2 text-sm text-gray-500'>Loading plans...</span>
                </div>
              ) : chPlans.length === 0 ? (
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-4 text-center'>
                  <p className='text-sm text-gray-500'>No filing plans available. You can proceed without a plan.</p>
                </div>
              ) : (
                <div className='space-y-2'>
                  {chPlans.map((plan) => {
                    const isSelected = selectedPlanSlug === plan.slug
                    return (
                      <button
                        key={plan.slug}
                        type='button'
                        onClick={() => setSelectedPlanSlug(plan.slug)}
                        className={`flex w-full items-center gap-4 rounded-xl border-2 p-4 text-left transition-all ${
                          isSelected
                            ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div
                          className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
                            isSelected ? 'bg-gray-900' : 'border-2 border-gray-300'
                          }`}
                        >
                          {isSelected && <Check className='h-3 w-3 text-white' />}
                        </div>
                        <div className='flex-1'>
                          <span className='text-sm font-semibold text-gray-900'>{plan.name}</span>
                          <span className='ml-2 text-sm font-bold text-gray-900'>
                            {new Intl.NumberFormat('en-GB', { style: 'currency', currency: plan.currency }).format(plan.price)}
                          </span>
                          {plan.features.length > 0 && (
                            <p className='mt-0.5 text-xs text-gray-500'>{plan.features[0]}</p>
                          )}
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )

      // ----- Fill Form -----
      case 'fill-form':
        if (filingType === 'registered-office') {
          return (
            <div className='space-y-6'>
              <div>
                <h2 className='text-xl font-bold text-gray-900'>New Registered Office Address</h2>
                <p className='mt-1 text-sm text-gray-600'>
                  Enter the new registered office address for company <strong>{companyNumber}</strong>.
                </p>
              </div>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>Premises / Building Name</label>
                  <input
                    type='text'
                    value={address.premises || ''}
                    onChange={(e) => setAddress((prev) => ({ ...prev, premises: e.target.value }))}
                    maxLength={50}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                  />
                </div>
                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>
                    Address Line 1 <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    value={address.address_line_1}
                    onChange={(e) => setAddress((prev) => ({ ...prev, address_line_1: e.target.value }))}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                  />
                </div>
                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>Address Line 2</label>
                  <input
                    type='text'
                    value={address.address_line_2 || ''}
                    onChange={(e) => setAddress((prev) => ({ ...prev, address_line_2: e.target.value }))}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                  />
                </div>
                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>
                    Town / City <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    value={address.locality}
                    onChange={(e) => setAddress((prev) => ({ ...prev, locality: e.target.value }))}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                  />
                </div>
                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>County / Region</label>
                  <input
                    type='text'
                    value={address.region || ''}
                    onChange={(e) => setAddress((prev) => ({ ...prev, region: e.target.value }))}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                  />
                </div>
                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>
                    Postcode <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    value={address.postal_code}
                    onChange={(e) => setAddress((prev) => ({ ...prev, postal_code: e.target.value.toUpperCase() }))}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black uppercase focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                  />
                </div>
                <div>
                  <label className='mb-2 block text-sm font-medium text-gray-900'>Country</label>
                  <input
                    type='text'
                    value={address.country || 'United Kingdom'}
                    onChange={(e) => setAddress((prev) => ({ ...prev, country: e.target.value }))}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                  />
                </div>
              </div>

              <label className='flex items-start justify-between gap-3 cursor-pointer rounded-lg border border-gray-200 p-4 hover:bg-gray-50 transition-colors'>
                <input
                  type='checkbox'
                  checked={address.accept_appropriate_office_address_statement}
                  onChange={(e) => setAddress((prev) => ({ ...prev, accept_appropriate_office_address_statement: e.target.checked }))}
                  className='mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900'
                />
                <div>
                  <span className='text-sm font-medium text-gray-900'>Appropriate Address Statement</span>
                  <p className='text-xs text-gray-500'>
                    I confirm that the address above is an appropriate address as defined under section 86(2) of the Companies Act 2006.
                  </p>
                </div>
                <div />
              </label>
            </div>
          )
        }

        // Email form
        return (
          <div className='space-y-6'>
            <div>
              <h2 className='text-xl font-bold text-gray-900'>New Registered Email Address</h2>
              <p className='mt-1 text-sm text-gray-600'>
                Enter the new registered email address for company <strong>{companyNumber}</strong>.
              </p>
            </div>

            <div>
              <label className='mb-2 block text-sm font-medium text-gray-900'>
                Registered Email Address <span className='text-red-500'>*</span>
              </label>
              <input
                type='email'
                value={emailData.registered_email_address}
                onChange={(e) => setEmailData((prev) => ({ ...prev, registered_email_address: e.target.value }))}
                placeholder='e.g., company@example.com'
                className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
              />
            </div>

            <label className='flex items-start justify-between gap-3 cursor-pointer rounded-lg border border-gray-200 p-4 hover:bg-gray-50 transition-colors'>
              <input
                type='checkbox'
                checked={emailData.accept_appropriate_email_address_statement}
                onChange={(e) => setEmailData((prev) => ({ ...prev, accept_appropriate_email_address_statement: e.target.checked }))}
                className='mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900'
              />
              <div>
                <span className='text-sm font-medium text-gray-900'>Appropriate Email Address Statement</span>
                <p className='text-xs text-gray-500'>
                  I confirm that the email address above is an appropriate email address as defined under the Companies Act 2006.
                </p>
              </div>
              <div />
            </label>
          </div>
        )

      // ----- Review -----
      case 'review': {
        const officeLines = formatRegisteredOffice(csDetails?.registeredOffice ?? selectedCompany?.registeredOffice)
        const sicList = formatSicCodesList(csDetails?.sicCodes ?? selectedCompany?.sicCodes ?? [])
        const csDirectors = csDetails?.directors ?? []
        const csPscs = csDetails?.psc ?? []
        return (
          <div className='space-y-6'>
            <div>
              <h2 className='text-xl font-bold text-gray-900'>Review Company Details</h2>
              <p className='mt-1 text-sm text-gray-600'>
                Please review the company information below. If any details are incorrect, you'll need to update them with Companies House before submitting your Confirmation Statement through BritFiler.
              </p>
            </div>

            {/* Registered Office Address */}
            <section className='rounded-lg border border-gray-200 bg-white'>
              <div className='flex items-center gap-2 border-b border-gray-100 px-5 py-3'>
                <MapPin className='h-4 w-4 text-gray-500' />
                <h3 className='text-sm font-semibold text-gray-900'>Registered Office Address</h3>
              </div>
              <div className='px-5 py-4'>
                {officeLines.length > 0 ? (
                  <div className='space-y-0.5 text-sm text-gray-900'>
                    {officeLines.map((line, i) => <p key={i}>{line}</p>)}
                  </div>
                ) : (
                  <p className='text-sm text-gray-500'>No registered office address on record.</p>
                )}
                <div className='mt-3 flex items-start gap-2 text-xs text-gray-500'>
                  <Info className='mt-0.5 h-4 w-4 flex-shrink-0' />
                  <p>Need to update the registered office address? File Form AD01 with Companies House before continuing.</p>
                </div>
              </div>
            </section>

            {/* Directors & Officers — live from Companies House */}
            <section className='rounded-lg border border-gray-200 bg-white'>
              <div className='flex items-center gap-2 border-b border-gray-100 px-5 py-3'>
                <Shield className='h-4 w-4 text-gray-500' />
                <h3 className='text-sm font-semibold text-gray-900'>Directors &amp; Officers</h3>
              </div>
              <div className='px-5 py-4'>
                {isLoadingCsDetails ? (
                  <p className='text-sm text-gray-500'>Loading…</p>
                ) : csDirectors.length > 0 ? (
                  <ul className='space-y-2 text-sm text-gray-900'>
                    {csDirectors.map((d, i) => (
                      <li key={`${d.name}-${i}`}>
                        <span className='font-medium'>{d.name}</span> — {d.role}{' '}
                        <span className='text-gray-500'>(appointed {fmtCsDate(d.appointedOn)})</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className='text-sm text-gray-500'>No active directors on record.</p>
                )}
                <div className='mt-3 flex items-start gap-2 text-xs text-gray-500'>
                  <Info className='mt-0.5 h-4 w-4 flex-shrink-0' />
                  <p>To appoint a new director or update director details, file the appropriate Companies House form (such as AP01 or TM01) before submitting your Confirmation Statement.</p>
                </div>
              </div>
            </section>

            {/* SIC Codes */}
            <section className='rounded-lg border border-gray-200 bg-white'>
              <div className='flex items-center justify-between border-b border-gray-100 px-5 py-3'>
                <h3 className='text-sm font-semibold text-gray-900'>SIC Codes</h3>
                <button
                  type='button'
                  onClick={openSicEditor}
                  disabled={!selectedCompany}
                  className='inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50 transition'
                >
                  <Pencil className='h-4 w-4' />
                  Edit SIC codes
                </button>
              </div>
              <div className='px-5 py-4'>
                {sicList.length > 0 ? (
                  <ul className='space-y-1 text-sm text-gray-900'>
                    {sicList.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                ) : (
                  <p className='text-sm text-gray-500'>No SIC codes on record.</p>
                )}
              </div>
            </section>

            {/* People with Significant Control — live from Companies House */}
            <section className='rounded-lg border border-gray-200 bg-white'>
              <div className='border-b border-gray-100 px-5 py-3'>
                <h3 className='text-sm font-semibold text-gray-900'>People with Significant Control</h3>
              </div>
              <div className='px-5 py-4'>
                {isLoadingCsDetails ? (
                  <p className='text-sm text-gray-500'>Loading…</p>
                ) : csPscs.length > 0 ? (
                  <ul className='space-y-3 text-sm'>
                    {csPscs.map((p, i) => (
                      <li key={`${p.name}-${i}`}>
                        <span className='font-medium text-gray-900'>{p.name}</span>
                        {p.summary && <span className='text-gray-500'> — {p.summary}</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className='text-sm text-gray-500'>No people with significant control on record.</p>
                )}
                <div className='mt-3 flex items-start gap-2 text-xs text-gray-500'>
                  <Info className='mt-0.5 h-4 w-4 flex-shrink-0' />
                  <p>Need to update your PSC information? Please update it with Companies House before continuing.</p>
                </div>
              </div>
            </section>

            {/* Director Identity Verification — live status from Companies House */}
            {csDirectors.length > 0 && (
            <section className='rounded-lg border border-gray-200 bg-white'>
              <div className='flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-3'>
                <div className='flex items-center gap-2'>
                  <Shield className='h-4 w-4 text-gray-500' />
                  <h3 className='text-sm font-semibold text-gray-900'>Director Identity Verification</h3>
                </div>
                <button
                  type='button'
                  onClick={() => selectedCompany && loadCsDetails(selectedCompany.companyNumber)}
                  disabled={isLoadingCsDetails}
                  className='inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition'
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isLoadingCsDetails ? 'animate-spin' : ''}`} />
                  Recheck status
                </button>
              </div>
              <div className='px-5 py-4 space-y-4'>
                <p className='text-sm text-gray-600'>
                  Companies House requires every director to verify their identity before a Confirmation Statement can be filed. Each status below is read live from Companies House.
                </p>

                <div className='overflow-x-auto'>
                  <table className='w-full text-sm'>
                    <thead>
                      <tr className='border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-500'>
                        <th className='py-2 pr-4 font-medium'>Director</th>
                        <th className='py-2 font-medium'>Verification Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csDirectors.map((d, i) => (
                        <tr key={`${d.name}-${i}`} className='border-b border-gray-100 last:border-0'>
                          <td className='py-3 pr-4 font-medium text-gray-900'>{d.name}</td>
                          <td className='py-3'>
                            {d.verified ? (
                              <span className='inline-flex items-center gap-1.5 text-green-700'>
                                <CheckCircle className='h-4 w-4' /> Verified
                              </span>
                            ) : d.requiresVerification === false ? (
                              <span className='text-gray-500'>Not required (corporate director)</span>
                            ) : (
                              <span className='inline-flex items-center gap-1.5 text-red-600'>
                                <XCircle className='h-4 w-4' /> Not Verified
                                {d.verificationDueOn && (
                                  <span className='text-xs text-gray-500'>· due {fmtCsDate(d.verificationDueOn)}</span>
                                )}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {directorsNeedingVerification.map((d, i) => {
                  const code = personalCodes[d.name] ?? ''
                  const dob = directorDobs[d.name] ?? ''
                  const dobMismatch = isPastIsoDate(dob) && !dobMatchesRegister(d, dob)
                  return (
                    <div key={`${d.name}-nv-${i}`} className='space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4'>
                      <div className='flex items-start gap-2 text-sm text-amber-900'>
                        <AlertCircle className='mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600' />
                        <div>
                          <p className='font-semibold'>{d.name}</p>
                          <p className='mt-1'>
                            This director hasn't confirmed their verified identity for this company yet. If they've verified with GOV.UK and have a Companies House personal code, enter it with their date of birth below — it is filed with this statement. Otherwise they need to verify first, then use “Recheck status” above.
                          </p>
                        </div>
                      </div>

                      <a
                        href={COMPANIES_HOUSE_VERIFY_URL}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 transition'
                      >
                        Verify Your Identity <ExternalLink className='h-4 w-4' />
                      </a>

                      <div className='grid gap-4 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-2'>
                        <div>
                          <label htmlFor={`cs-personal-code-${i}`} className='mb-1 block text-sm font-medium text-gray-900'>
                            Companies House personal code
                          </label>
                          <p className='mb-2 text-xs text-gray-500'>The 11-character code (letters and numbers) issued after verification.</p>
                          <input
                            id={`cs-personal-code-${i}`}
                            type='text'
                            autoComplete='off'
                            value={code}
                            onChange={(e) =>
                              setPersonalCodes((prev) => ({
                                ...prev,
                                [d.name]: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11),
                              }))
                            }
                            placeholder='e.g. ABC12345678'
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 font-mono text-sm uppercase text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                          />
                          {code.length > 0 && code.length !== 11 && (
                            <p className='mt-1 text-xs text-red-600'>Personal codes are exactly 11 characters.</p>
                          )}
                        </div>
                        <div>
                          <label htmlFor={`cs-dob-${i}`} className='mb-1 block text-sm font-medium text-gray-900'>
                            Date of birth
                          </label>
                          <p className='mb-2 text-xs text-gray-500'>
                            {d.dateOfBirth
                              ? `Full date — the register shows ${fmtMonthYear(d.dateOfBirth)}.`
                              : 'Full date of birth, as given when verifying.'}
                          </p>
                          <input
                            id={`cs-dob-${i}`}
                            type='date'
                            value={dob}
                            max={new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setDirectorDobs((prev) => ({ ...prev, [d.name]: e.target.value }))}
                            className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                          />
                          {dobMismatch && d.dateOfBirth && (
                            <p className='mt-1 text-xs text-red-600'>
                              Doesn't match the register ({fmtMonthYear(d.dateOfBirth)}).
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}

                {allDirectorsVerified ? (
                  <p className='flex items-center gap-1.5 text-sm font-medium text-green-700'>
                    <CheckCircle className='h-4 w-4' /> All directors have verified their identity.
                  </p>
                ) : directorsReady ? (
                  <p className='flex items-center gap-1.5 text-sm font-medium text-green-700'>
                    <CheckCircle className='h-4 w-4' /> Verification details will be filed with this Confirmation Statement.
                  </p>
                ) : !csCapability.feeIncluded && verificationDetailsComplete ? (
                  <p className='text-sm text-red-700'>
                    {csCapability.checkFailed
                      ? "We couldn't check how this statement will be filed, so the verification details can't be sent yet. Reload the page to try again."
                      : "Filing with a director's personal code isn't available right now. Please contact BritFiler support."}
                  </p>
                ) : (
                  <p className='text-xs text-amber-700'>
                    Companies House won't accept this statement until every director is verified — enter each
                    director's personal code and date of birth above.
                  </p>
                )}
              </div>
            </section>
            )}

            {/* Companies House authentication code — only needed on the
                gateway channel, where we file under our presenter account and
                CH authenticates the company by its own code. */}
            {csCapability.requiresCompanyAuthCode && (
              <section className='rounded-lg border border-gray-200 bg-white'>
                <div className='flex items-center gap-2 border-b border-gray-100 px-5 py-3'>
                  <Shield className='h-4 w-4 text-gray-500' />
                  <h3 className='text-sm font-semibold text-gray-900'>Companies House authentication code</h3>
                </div>
                <div className='px-5 py-4'>
                  <input
                    type='text'
                    value={companyAuthCode}
                    onChange={e => setCompanyAuthCode(e.target.value)}
                    placeholder='6-character code'
                    maxLength={8}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-black focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900'
                  />
                  <p className='mt-2 text-xs text-gray-500'>
                    The code Companies House posted to your registered office. It authorises this filing.
                  </p>
                </div>
              </section>
            )}

            {/* Platform Filing Plan */}
            {chPlans.length > 0 && (
              <section className='rounded-lg border border-gray-200 bg-white'>
                <div className='flex items-center gap-2 border-b border-gray-100 px-5 py-3'>
                  <Sparkles className='h-4 w-4 text-gray-500' />
                  <h3 className='text-sm font-semibold text-gray-900'>
                    {csCapability.feeIncluded ? 'Price' : 'Platform Filing Plan'}
                  </h3>
                </div>
                <div className='space-y-2 px-5 py-4'>
                  {chPlans.map((plan) => {
                    const isSelected = selectedPlanSlug === plan.slug
                    return (
                      <button
                        key={plan.slug}
                        type='button'
                        onClick={() => setSelectedPlanSlug(plan.slug)}
                        className={`flex w-full items-center gap-4 rounded-xl border-2 p-4 text-left transition-all ${
                          isSelected ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${isSelected ? 'bg-gray-900' : 'border-2 border-gray-300'}`}>
                          {isSelected && <Check className='h-3 w-3 text-white' />}
                        </div>
                        <div className='flex-1'>
                          <span className='text-sm font-semibold text-gray-900'>{plan.name}</span>
                          <span className='ml-2 text-sm font-bold text-gray-900'>
                            {new Intl.NumberFormat('en-GB', { style: 'currency', currency: plan.currency }).format(plan.price)}
                          </span>
                          {plan.features.length > 0 && <p className='mt-0.5 text-xs text-gray-500'>{plan.features[0]}</p>}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </section>
            )}

            {/* Platform Payment Notice */}
            {chPlans.length > 0 && !isPaid && (
              <div className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
                <div className='flex gap-3'>
                  <Info className='h-5 w-5 flex-shrink-0 text-blue-600 mt-0.5' />
                  <div>
                    <p className='text-sm font-medium text-blue-900'>
                      {csCapability.feeIncluded ? 'One payment covers everything' : 'Platform Filing Fee'}
                    </p>
                    {csCapability.feeIncluded ? (
                      <p className='mt-1 text-xs text-blue-700 leading-relaxed'>
                        Submitting will take you to a single payment that <strong>includes the £50 Companies House filing fee</strong> — we pay Companies House on your behalf, so there is nothing further to pay.
                      </p>
                    ) : (
                      <p className='mt-1 text-xs text-blue-700 leading-relaxed'>
                        Submitting will redirect you to pay the <strong>BritFiler platform fee</strong>{selectedPlanSlug ? ` (${selectedPlanSlug} plan)` : ''} for processing your Confirmation Statement. This is <strong>not</strong> a payment to Companies House.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Confirmation checkbox */}
            <label className='flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 cursor-pointer'>
              <input
                type='checkbox'
                checked={confirmReviewed}
                onChange={(e) => setConfirmReviewed(e.target.checked)}
                className='mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900'
              />
              <span className='text-sm text-gray-700'>
                I confirm that I've reviewed the company information above and understand that any changes must be made with Companies House before submitting this Confirmation Statement.
              </span>
            </label>
          </div>
        )
      }

      // ----- Success -----
      case 'success': {
        const rejected = filingStatus === 'rejected' || filingStatus === 'failed'
        const accepted = filingStatus === 'accepted'
        // A gateway filing Companies House has received but not yet decided.
        const awaitingCh = !!submittedFiling && !rejected && !accepted
        const rejections = submittedFiling?.rejections ?? []
        return (
          <div className='space-y-6 text-center py-8'>
            {rejected ? (
              <XCircle className='mx-auto h-16 w-16 text-red-600' />
            ) : (
              <CheckCircle className={`mx-auto h-16 w-16 ${accepted ? 'text-green-600' : 'text-blue-600'}`} />
            )}
            <h2 className={`text-2xl font-bold ${rejected ? 'text-red-900' : accepted ? 'text-green-900' : 'text-blue-900'}`}>
              {rejected
                ? 'Confirmation Statement Rejected'
                : accepted
                  ? 'Confirmation Statement Accepted'
                  : awaitingCh
                    ? 'Submitted to Companies House'
                    : 'Confirmation Statement Received'}
            </h2>
            <p className='text-sm text-gray-600 max-w-md mx-auto'>
              {rejected
                ? submittedFiling?.statusMessage || 'Companies House rejected the submission. Please review the company details and try again.'
                : accepted
                  ? 'Your Confirmation Statement has been accepted by Companies House.'
                  : awaitingCh
                    ? 'Companies House has received your Confirmation Statement and will accept or reject it — usually within 24 hours. We check automatically; you can follow it under Filings → Confirmation Statement.'
                    : resultMessage || 'Your Confirmation Statement has been received.'}
            </p>
            {rejections.length > 0 && (
              <ul className='mx-auto max-w-md space-y-1 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-left text-sm text-red-800'>
                {rejections.map((r, i) => (
                  <li key={`${r.code}-${i}`}>
                    {r.message} <span className='text-xs text-red-500'>({r.code})</span>
                  </li>
                ))}
              </ul>
            )}
            {resultTransactionId && (
              <div className='inline-block rounded-lg bg-gray-100 px-4 py-2'>
                <span className='text-xs text-gray-600'>{submittedFiling ? 'Submission number: ' : 'Reference: '}</span>
                <span className='text-sm font-mono font-semibold text-gray-900'>{resultTransactionId}</span>
              </div>
            )}
            <div className='flex flex-wrap items-center justify-center gap-3 pt-4'>
              {awaitingCh && (
                <button
                  type='button'
                  onClick={refreshSubmittedFiling}
                  disabled={isCheckingFilingStatus}
                  className='inline-flex items-center gap-2 rounded-lg border border-gray-300 px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors'
                >
                  <RefreshCw className={`h-4 w-4 ${isCheckingFilingStatus ? 'animate-spin' : ''}`} />
                  Check status
                </button>
              )}
              <button
                type='button'
                onClick={() => {
                  clearChCsFlow()
                  setCurrentStep('company-select')
                  setFilingType(null)
                  setAddress({
                    premises: '',
                    address_line_1: '',
                    address_line_2: '',
                    locality: '',
                    region: '',
                    postal_code: '',
                    country: 'United Kingdom',
                    accept_appropriate_office_address_statement: false,
                  })
                  setEmailData({
                    registered_email_address: '',
                    accept_appropriate_email_address_statement: false,
                  })
                  setResultTransactionId(null)
                  setFilingStatus(null)
                  setSubmittedFiling(null)
                  setResultMessage(null)
                  setPersonalCodes({})
                  setDirectorDobs({})
                  setConfirmReviewed(false)
                }}
                className='rounded-lg bg-gray-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors'
              >
                File Another Confirmation Statement
              </button>
            </div>
          </div>
        )
      }

      default:
        return null
    }
  }

  // ========================================================
  // JSX
  // ========================================================

  return (
    <AuthenticatedLayout>
      <div className='min-h-screen bg-gradient-to-b from-gray-50 to-white'>
        {/* Header */}
        <div className='border-b border-gray-200 bg-white sticky top-0 z-40'>
          <div className='mx-auto  px-6 py-4'>
            <div className='relative flex items-center justify-center'>
              <button
                onClick={handleHeaderBack}
                aria-label={currentStep === 'company-select' ? 'Go back' : 'Back to select company'}
                className='absolute left-0 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors'
              >
                <ArrowLeft className='h-5 w-5' />
              </button>
              <div className='text-center'>
                <h1 className='text-lg font-bold text-gray-900'>Confirmation Statement</h1>
                <p className='text-xs text-gray-500'>File your annual Confirmation Statement with Companies House</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className='mx-auto px-6 py-8'>
          {/* Step indicators */}
          {currentStep !== 'success' && (
            <div className='mb-8 flex items-center justify-between'>
              {stepOrder.filter((s) => s !== 'success').map((step) => (
                <StepIndicator
                  key={step}
                  step={step}
                  label={stepLabels[step]}
                  currentStep={currentStep}
                  stepOrder={stepOrder}
                />
              ))}
            </div>
          )}

          {/* Step content */}
          <div className='rounded-2xl border border-gray-200 bg-white p-8 shadow-sm'>
            {renderContent()}

            {/* Navigation buttons */}
            {currentStep !== 'success' && (
              <div className='mt-8 flex items-center justify-between border-t border-gray-200 pt-6'>
                <button
                  type='button'
                  onClick={handleBack}
                  disabled={currentStep === 'company-select'}
                  className='flex items-center gap-2 rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  <ArrowLeft className='h-4 w-4' />
                  Back
                </button>

                {currentStep === 'review' ? (
                  <button
                    type='button'
                    onClick={handleSubmit}
                    disabled={isSubmitting || !canAdvance()}
                    className='flex items-center gap-2 rounded-lg bg-gray-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    {isSubmitting ? (
                      <Loader2 className='h-4 w-4 animate-spin' />
                    ) : chPlans.length > 0 && !isPaid ? (
                      <CreditCard className='h-4 w-4' />
                    ) : (
                      <Send className='h-4 w-4' />
                    )}
                    {isSubmitting
                      ? (chPlans.length > 0 && !isPaid ? 'Redirecting to Payment...' : 'Submitting...')
                      : (chPlans.length > 0 && !isPaid ? 'Confirm & Pay' : 'Confirm & Submit')}
                  </button>
                ) : (
                  <button
                    type='button'
                    onClick={handleNext}
                    disabled={!canAdvance()}
                    className='flex items-center gap-2 rounded-lg bg-gray-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 transition-colors'
                  >
                    Next
                    <ArrowRight className='h-4 w-4' />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <CompaniesHouseAuthModal
        isOpen={isAuthModalOpen}
        onClose={() => {
          setIsAuthModalOpen(false)
          checkTokenStatus()
        }}
        authUrl={authUrl}
        isRequestingAuth={isRequestingAuth}
        onAuthorize={handleRequestAuth}
        companyNumber={companyNumber}
      />

      {/* SIC codes editor (inline — no navigation away from the flow) */}
      {sicModalOpen && (
        <div
          className='fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4'
          onClick={() => !isSavingSic && setSicModalOpen(false)}
        >
          <div
            className='flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl'
            role='dialog'
            aria-modal='true'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='flex items-center justify-between border-b border-gray-200 px-5 py-4'>
              <h3 className='text-base font-semibold text-gray-900'>Edit SIC codes</h3>
              <button
                type='button'
                onClick={() => setSicModalOpen(false)}
                className='flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:text-gray-900'
              >
                ×
              </button>
            </div>

            <div className='flex-1 space-y-4 overflow-y-auto px-5 py-4'>
              <div>
                <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>Current SIC codes</p>
                {editedSicCodes.length > 0 ? (
                  <ul className='space-y-2'>
                    {editedSicCodes.map((code) => (
                      <li key={code} className='flex items-start justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2'>
                        <span className='text-sm text-gray-900'>
                          <span className='font-medium'>{code}</span>
                          <span className='text-gray-500'> — {getSicCodeDescription(code)}</span>
                        </span>
                        <button
                          type='button'
                          onClick={() => setEditedSicCodes((prev) => prev.filter((c) => c !== code))}
                          className='mt-0.5 text-gray-400 hover:text-red-600'
                          aria-label={`Remove ${code}`}
                        >
                          <XCircle className='h-4 w-4' />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className='text-sm text-gray-500'>No SIC codes selected.</p>
                )}
              </div>

              <div>
                <label className='mb-1 block text-sm font-medium text-gray-900'>Add a SIC code</label>
                <input
                  value={sicSearch}
                  onChange={(e) => setSicSearch(e.target.value)}
                  placeholder='Search by code or activity (e.g. 62012 or software)'
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400'
                />
                {sicSearch.trim() && (() => {
                  const matches = searchSicCodes(sicSearch, 10, editedSicCodes)
                  return (
                    <ul className='mt-2 max-h-52 overflow-y-auto rounded-lg border border-gray-100'>
                      {matches.length > 0 ? (
                        matches.map((s) => (
                          <li key={s.code}>
                            <button
                              type='button'
                              onClick={() => {
                                setEditedSicCodes((prev) => [...prev, s.code])
                                setSicSearch('')
                              }}
                              className='flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50'
                            >
                              <span className='font-medium text-gray-900'>{s.code}</span>
                              <span className='text-gray-500'>{s.description}</span>
                            </button>
                          </li>
                        ))
                      ) : (
                        <li className='px-3 py-2 text-sm text-gray-500'>No matching SIC codes.</li>
                      )}
                    </ul>
                  )
                })()}
              </div>

              <p className='text-xs text-gray-500'>
                Changes are saved to your BritFiler company record and confirmed when you file this Confirmation Statement.
              </p>
            </div>

            <div className='flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4'>
              <button
                type='button'
                onClick={() => setSicModalOpen(false)}
                disabled={isSavingSic}
                className='rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={saveSicCodes}
                disabled={isSavingSic}
                className='rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50'
              >
                {isSavingSic ? 'Saving…' : 'Save SIC codes'}
              </button>
            </div>
          </div>
        </div>
      )}
      <TrustpilotReviewModal
        open={showReviewPrompt}
        onClose={() => setShowReviewPrompt(false)}
        filingLabel='Confirmation Statement'
      />
    </AuthenticatedLayout>
  )
}





